import { DirectionType, Direction } from '@wui/common-library'
import { Ref, isRef, nextTick, ref } from 'vue'

type Config = {
  event: MouseEvent | TouchEvent,
  /** Required unless `fixed` — absolute menus are positioned relative to this element. */
  container?: Ref<HTMLElement | null>,
  popup: Ref<HTMLElement | null>,
  gapX?: number,
  gapY?: number,
  isCentered?: boolean,
  /**
   * Viewport coords for `position: fixed` (teleported menus).
   * Use when ancestors clip with overflow-hidden (e.g. left sidebar).
   */
  fixed?: boolean,
}

const getEventClientPoint = (event: MouseEvent | TouchEvent) => {
  if ('clientY' in event) {
    return { clientX: event.clientX, clientY: event.clientY }
  }

  const touch = event.touches[0]
  return {
    clientX: touch?.clientX ?? 0,
    clientY: touch?.clientY ?? 0,
  }
}

/**
 * @TODO add support for Vue components for popup (now popup.value.style is undefined)
 *
 * @param config @see Config
 * @returns void
 */
export const usePositionPopUp = async (config: Config) => {
  const {
    event,
    container,
    popup,
    gapY = 0,
    gapX = 0,
    isCentered = false,
    fixed = false,
  } = config

  await nextTick()
  if (!popup.value) {
    return
  }
  if (!fixed && !container?.value) {
    return
  }

  const menuRect = popup.value.getBoundingClientRect()
  const { clientX, clientY } = getEventClientPoint(event)

  const doesYFit = clientY + menuRect.height + gapY <= window.innerHeight
  const doesXFit = clientX + menuRect.width + gapX <= window.innerWidth
  const maxViewportTop = Math.max(0, window.innerHeight - menuRect.height)
  const maxViewportLeft = Math.max(0, window.innerWidth - menuRect.width)

  if (fixed) {
    let top = doesYFit ? clientY - gapY : (clientY - menuRect.height) + gapY
    let left = doesXFit ? clientX - gapX : (clientX - menuRect.width) + gapX
    if (left < 0) {
      left = clientX - gapX
    }

    top = Math.min(Math.max(0, top), maxViewportTop)
    left = Math.min(Math.max(0, left), maxViewportLeft)

    popup.value.style.top = `${top}px`
    popup.value.style.left = isCentered ? '50%' : `${left}px`
    popup.value.style.transform = isCentered ? 'translate(-50%)' : ''
    return
  }

  const containerRect = container!.value!.getBoundingClientRect()
  const yCoordinate = clientY - containerRect.top
  const xCoordinate = clientX - containerRect.left

  // Fit against the viewport: container (e.g. folder bar) is often shorter than the menu,
  // so container-only checks always "fail" and misplace the popup.
  let top = doesYFit ? yCoordinate - gapY : (yCoordinate - menuRect.height) + gapY
  let left = doesXFit ? xCoordinate - gapX : (xCoordinate - menuRect.width) + gapX
  if (left < 0) {
    left = xCoordinate - gapX
  }

  // Flip alone is not enough near the top/left: a tall menu still overflows.
  // Clamp in viewport space, then convert back to container-relative coords.
  top = Math.min(Math.max(0, top + containerRect.top), maxViewportTop) - containerRect.top
  left = Math.min(Math.max(0, left + containerRect.left), maxViewportLeft) - containerRect.left

  popup.value.style.top = `${top}px`
  popup.value.style.left = isCentered ? '50%' : `${left}px`
  popup.value.style.transform = isCentered ? 'translate(-50%)' : ''
}

type DirectionConfig = {
  activator: Ref<HTMLElement | null>,
  popup: Ref<HTMLElement | null>,
  direction: Ref<DirectionType | undefined> | DirectionType,
  offset?: number,
  clampToViewport?: boolean,
}

const getMenuPosition = (
  directionValue: DirectionType,
  triggerRect: DOMRect,
  menuRect: DOMRect,
  offset: number,
) => {
  const centerX = triggerRect.left + triggerRect.width / 2 - menuRect.width / 2
  const centerY = triggerRect.top + triggerRect.height / 2 - menuRect.height / 2

  const positions: Record<DirectionType, { top: number; left: number }> = {
    [Direction.TOP]: { top: triggerRect.top - menuRect.height - offset, left: centerX },
    [Direction.TOP_LEFT]: { top: triggerRect.top - menuRect.height - offset, left: triggerRect.left },
    [Direction.TOP_RIGHT]: { top: triggerRect.top - menuRect.height - offset, left: triggerRect.right - menuRect.width },
    [Direction.BOTTOM]: { top: triggerRect.bottom + offset, left: centerX },
    [Direction.BOTTOM_LEFT]: { top: triggerRect.bottom + offset, left: triggerRect.left },
    [Direction.BOTTOM_RIGHT]: { top: triggerRect.bottom + offset, left: triggerRect.right - menuRect.width },
    [Direction.LEFT]: { top: centerY, left: triggerRect.left - menuRect.width - offset },
    [Direction.LEFT_TOP]: { top: triggerRect.top, left: triggerRect.left - menuRect.width - offset },
    [Direction.LEFT_BOTTOM]: { top: triggerRect.bottom - menuRect.height, left: triggerRect.left - menuRect.width - offset },
    [Direction.RIGHT]: { top: centerY, left: triggerRect.right + offset },
    [Direction.RIGHT_TOP]: { top: triggerRect.top, left: triggerRect.right + offset },
    [Direction.RIGHT_BOTTOM]: { top: triggerRect.bottom - menuRect.height, left: triggerRect.right + offset },
  }

  return positions[directionValue]
}

export const usePositionPopUpByDirection = (config: DirectionConfig) => {
  const {
    activator,
    popup,
    direction,
    offset = 8,
    clampToViewport = true,
  } = config

  const popupStyles = ref<Record<string, string>>({})

  const updatePosition = async () => {
    await nextTick()
    if (!activator.value || !popup.value) {
      return
    }

    const triggerRect = activator.value.getBoundingClientRect()
    const menuRect = popup.value.getBoundingClientRect()
    const directionValue = isRef(direction) ? (direction.value ?? Direction.TOP_LEFT) : (direction ?? Direction.TOP_LEFT)
    const position = getMenuPosition(directionValue, triggerRect, menuRect, offset)

    let top = position.top
    let left = position.left

    if (clampToViewport) {
      top = Math.min(Math.max(0, top), window.innerHeight - menuRect.height)
      left = Math.min(Math.max(0, left), window.innerWidth - menuRect.width)
    }

    popupStyles.value = {
      top: `${top}px`,
      left: `${left}px`,
    }
  }

  return {
    popupStyles,
    updatePosition,
  }
}
